import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ScrollArea } from "../ui/scroll-area";
import { formatDateTimeIST } from "../../utils/dateUtils";
import { getStatusIcon, getOperationIcon } from "./nocUtils";
import { Search, Filter } from "lucide-react";

export const OperationLogsTab = ({
  logs,
  filters,
  setFilters,
  pagination,
  setPagination,
  fetchLogs
}) => {
  return (
    <div className="space-y-4" data-testid="operation-logs-tab">
      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filters:</span>
            </div>
            <Select
              value={filters.operation_type}
              onValueChange={(v) => setFilters({...filters, operation_type: v === "all" ? "" : v})}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Operation Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="API_CALL">API Call</SelectItem>
                <SelectItem value="AI_ANALYSIS">AI Analysis</SelectItem>
                <SelectItem value="DB_OPERATION">DB Operation</SelectItem>
                <SelectItem value="AUTH">Authentication</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.status}
              onValueChange={(v) => setFilters({...filters, status: v === "all" ? "" : v})}
            >
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="SUCCESS">Success</SelectItem>
                <SelectItem value="FAILURE">Failure</SelectItem>
                <SelectItem value="ERROR">Error</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by email..."
                className="pl-9 w-[200px]"
                value={filters.user_email}
                onChange={(e) => setFilters({...filters, user_email: e.target.value})}
              />
            </div>
            <Button variant="outline" size="sm" onClick={fetchLogs}>
              Apply
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Operation Logs</CardTitle>
          <CardDescription>Showing {logs.length} of {pagination.total} operations</CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[500px]">
            <div className="space-y-2">
              {logs.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No logs found</p>
              ) : (
                logs.map((log) => (
                  <div
                    key={log.id}
                    className="p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-3">
                        {getStatusIcon(log.status)}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{log.function_name}</span>
                            <Badge variant="outline" className="text-xs">
                              {getOperationIcon(log.operation_type)}
                              <span className="ml-1">{log.operation_type}</span>
                            </Badge>
                            <Badge variant="secondary" className="text-xs">
                              {log.method}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {log.endpoint} • {log.user_email} ({log.user_role})
                          </p>
                          {log.response_summary && (
                            <p className="text-xs text-muted-foreground mt-1">
                              &rarr; {log.response_summary}
                            </p>
                          )}
                          {log.ai_prompt && (
                            <div className="mt-2 p-2 bg-blue-500/10 rounded text-xs">
                              <span className="font-medium text-blue-600">AI Prompt:</span>
                              <p className="text-muted-foreground">{log.ai_prompt}</p>
                            </div>
                          )}
                          {log.error_message && (
                            <p className="text-xs text-red-500 mt-1">
                              Error: {log.error_message}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-xs text-muted-foreground">{formatDateTimeIST(log.timestamp)}</p>
                        <p className="text-xs font-mono text-muted-foreground">{log.execution_time_ms.toFixed(1)}ms</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t">
            <p className="text-sm text-muted-foreground">
              Page {Math.floor(pagination.skip / pagination.limit) + 1} of {Math.ceil(pagination.total / pagination.limit)}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.skip === 0}
                onClick={() => setPagination(p => ({...p, skip: Math.max(0, p.skip - p.limit)}))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.skip + pagination.limit >= pagination.total}
                onClick={() => setPagination(p => ({...p, skip: p.skip + p.limit}))}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
